import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MantineColumn, MantineTable } from '../../../../../../react-commons/components/table/MantineTable';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { QueryBaseComponent } from '../../../../../../react-commons/query/QueryBaseComponent';
import { formatIdentifier } from '../../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import { formatEnum } from '../../../../resource/enumerations/utils/formatEnum';
import { VCycleElementType } from '../../../../resource/enumerations/VCycleElementType';
import { VCycleSide } from '../../../../resource/enumerations/VCycleSide';
import {
    defaultVCycleElementContainerResource,
    useVCycleElementContainerActions,
    VCycleElementContainerPropertyValidation,
    VCycleElementContainerResource,
} from '../../../../resource/resources/artifacts/use_case/representation/vcycleElementContainerResource';
import { EditDeleteButtons } from '../../../reusable-components/base/EditDeleteButtons';
import { ControlledPropertyInputs } from '../../../reusable-components/base/property-bases/read-only/ControlledPropertyInputs';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { VCycleElementContainerCreationDialog } from '../vcycle-element-container/VCycleElementContainerCreationDialog';
import { VCycleElementContainerEditDialog } from '../vcycle-element-container/VCycleElementContainerEditDialog';
import { NumberOfElementsInput } from './NumberOfElementsInput';

export const VCycleEditorWingTableComponent = ({ workflowId, side }: { workflowId: string; side: VCycleSide }) => {
    const { id: projectId, versionId } = useParams<{
        id: string;
        versionId: string;
    }>();

    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const nestedDialog = useNestedDialogFunction();

    const vcycleElementContainerActions = useVCycleElementContainerActions();
    const vcycleElementsQuery = vcycleElementContainerActions.useGetAllResources(workflowId);

    const deleteVCycleElement = vcycleElementContainerActions.useDeleteResource();

    const getElements = () => vcycleElementsQuery.data?.filter((value) => value.side === side) ?? [];

    const openEditDialog = (resource: VCycleElementContainerResource) => {
        nestedDialog.showNestedDialog(
            <VCycleElementContainerEditDialog
                onClose={nestedDialog.closeNestedDialog}
                objectId={resource.objectId}
                projectId={projectId}
                versionedInId={resource.versionedIn}
            />
        );
    };

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <VCycleElementContainerCreationDialog
                projectId={projectId}
                projectVersionId={versionId}
                onClose={nestedDialog.closeNestedDialog}
                parentId={workflowId}
                autoGeneratedProps={{
                    ...defaultVCycleElementContainerResource,
                    side,
                    index: getElements().length ? Math.max(...getElements().map((value) => value.index)) + 1 : 0,
                }}
            />
        );
    };

    const columns = useMemo((): Array<MantineColumn<VCycleElementContainerResource>> => {
        return [
            {
                Header: null,
                type: 'number',
                accessorKey: 'index',
                header: 'Index',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.Number
                        readOnly={true}
                        nullable={false}
                        value={original.index}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER,
                            propertyIdentifier: VCycleElementContainerPropertyValidation.INDEX,
                            resourceId: original.id,
                        }}
                    />
                ),
            },
            {
                Header: null,
                type: 'reference',
                accessorKey: 'rowSpan',
                header: 'Row Span',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.Number
                        readOnly={true}
                        nullable={false}
                        value={original.rowSpan}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER,
                            propertyIdentifier: VCycleElementContainerPropertyValidation.ROW_SPAN,
                            resourceId: original.id,
                        }}
                    />
                ),
            },
            {
                Header: null,
                type: 'reference',
                accessorKey: 'type',
                header: 'Type',
                Cell: ({ row: { original } }) => (
                    <ControlledPropertyInputs.String
                        readOnly={true}
                        nullable={false}
                        value={formatEnum(VCycleElementType, original.type)}
                        style={{ paddingLeft: 0 }}
                        hideLabel={true}
                        propertyValidationInformation={{
                            resourceType: ResourceType.V_CYCLE_ELEMENT_CONTAINER,
                            propertyIdentifier: VCycleElementContainerPropertyValidation.TYPE,
                            resourceId: original.id,
                        }}
                    />
                ),
            },
            {
                Header: null,
                type: 'reference',
                accessorKey: 'id',
                header: '# Elements',
                Cell: ({ row: { original } }) => (
                    <NumberOfElementsInput parentId={original.id} parentType={original.type} />
                ),
            },
        ];
    }, []);

    return (
        <QueryBaseComponent name="VCycleElements" query={vcycleElementsQuery}>
            {(data) => (
                <div
                    style={{
                        height: '100%',
                        minHeight: 485,
                        position: 'relative',
                        overflowY: 'auto',
                    }}
                >
                    <Group justify={'space-between'} px={8} pt={8} h={44}>
                        <Text>{`${formatIdentifier(side)} Wing Elements`}</Text>
                    </Group>
                    <MantineTable
                        columns={columns}
                        data={data.filter((value) => value.side === side).toSorted((a, b) => a.index - b.index)}
                        label={`${formatIdentifier(side)} Wing Elements`}
                        enableActions={true}
                        initialPageSize={10}
                        fillParent={false}
                        rowActionsWidth={60}
                        rowActionsHeader={
                            <Tooltip label={'Add'}>
                                <ActionIcon
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={openAddDialog}
                                    size={'sm'}
                                >
                                    <IconPlus />
                                </ActionIcon>
                            </Tooltip>
                        }
                        tableOptions={{
                            enableSorting: true,
                            enableBottomToolbar: false,
                            enablePagination: false,
                            enableStickyHeader: false,
                            enableTopToolbar: false,
                            autoResetPageIndex: false,
                            renderRowActions: ({ row: { original, index } }) => (
                                <EditDeleteButtons
                                    key={index}
                                    handleEdit={() => openEditDialog(original)}
                                    handleDelete={() => {
                                        deleteResourceDialogFunction.deleteResource(
                                            original.id,
                                            `At Index ${original.index}`,
                                            ResourceType.V_CYCLE_ELEMENT_CONTAINER,
                                            deleteVCycleElement
                                        );
                                    }}
                                />
                            ),
                        }}
                    />
                </div>
            )}
        </QueryBaseComponent>
    );
};
